import { Trans, useLingui } from "@lingui/react/macro";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Disclosure,
  NativeSelect,
  NativeSelectOption,
  Spinner,
  toast,
} from "@rakazo/ui-web";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryUserId } from "../../lib/query";
import { recheckSessionOnUnauthorized, withSpaceHeaders } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";
import { CheckRow } from "./check-row";
import type { KnowledgeConnectionValues } from "./knowledge-forms";
import {
  KnowledgeCollectionForm,
  KnowledgeConnectionForm,
  KnowledgeUploadForm,
} from "./knowledge-forms";

type Collection = Awaited<ReturnType<typeof drokRpc.knowledge.listCollections>>[number];
type Document = Awaited<ReturnType<typeof drokRpc.knowledge.listDocuments>>[number];
type SourceBot = Awaited<ReturnType<typeof drokRpc.templates.sourceBots>>[number];
type DocumentAction = { kind: "publish" | "withdraw"; document: Document };

function processing(document: Document) {
  return document.parseStatus === "pending" || document.parseStatus === "processing";
}

async function uploadDocument(
  collectionId: string,
  file: File,
  metadata: { title?: string; version: string; replacesDocumentId?: string },
): Promise<void> {
  const body = new FormData();
  body.append("file", file);
  body.append("version", metadata.version);
  if (metadata.title) body.append("title", metadata.title);
  if (metadata.replacesDocumentId) body.append("replacesDocumentId", metadata.replacesDocumentId);
  const response = await fetch(
    `/api/knowledge/collections/${encodeURIComponent(collectionId)}/documents`,
    { method: "POST", credentials: "include", headers: withSpaceHeaders(), body },
  );
  recheckSessionOnUnauthorized(response.status);
  if (!response.ok) {
    const result = await response.json().catch(() => null);
    const message = result?.error?.message ?? result?.message;
    throw new Error(typeof message === "string" ? message : `Upload failed (${response.status})`);
  }
}

function KnowledgeBindings({
  collection,
  bots,
  saving,
  error,
  onSave,
}: {
  collection: Collection;
  bots: SourceBot[];
  saving: boolean;
  error: string | null;
  onSave: (botIds: string[]) => void;
}) {
  const [selected, setSelected] = useState(collection.botIds);
  const changed =
    selected.length !== collection.botIds.length ||
    selected.some((id) => !collection.botIds.includes(id));
  return (
    <Disclosure
      summary={<Trans>Assistants</Trans>}
      summaryClassName="text-sm font-medium"
      contentClassName="pt-3"
    >
      <div className="space-y-3 rounded-xl border border-border p-4">
        {bots.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {bots.map((bot) => (
              <CheckRow
                key={bot.id}
                checked={selected.includes(bot.id)}
                disabled={saving}
                onChange={(checked) =>
                  setSelected((current) =>
                    checked ? [...current, bot.id] : current.filter((id) => id !== bot.id),
                  )
                }
              >
                {bot.name}
              </CheckRow>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            <Trans>No assistants available</Trans>
          </p>
        )}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <Button size="sm" disabled={saving || !changed} onClick={() => onSave(selected)}>
          {saving ? <Spinner aria-hidden="true" /> : null}
          <Trans>Save assistants</Trans>
        </Button>
      </div>
    </Disclosure>
  );
}

function DocumentStatus({ document }: { document: Document }) {
  if (document.publicationStatus === "published")
    return (
      <Badge>
        <Trans>Published</Trans>
      </Badge>
    );
  if (document.publicationStatus === "withdrawn")
    return (
      <Badge variant="outline">
        <Trans>Withdrawn</Trans>
      </Badge>
    );
  if (document.parseStatus === "failed")
    return (
      <Badge variant="destructive">
        <Trans>Failed</Trans>
      </Badge>
    );
  if (document.parseStatus === "cancelled")
    return (
      <Badge variant="outline">
        <Trans>Cancelled</Trans>
      </Badge>
    );
  if (processing(document))
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Spinner className="size-3" aria-hidden="true" />
        <Trans>Processing</Trans>
      </span>
    );
  return (
    <Badge variant="outline">
      <Trans>Ready for review</Trans>
    </Badge>
  );
}

function DocumentCard({
  document,
  busy,
  onAction,
  onRetry,
  onNewVersion,
}: {
  document: Document;
  busy: boolean;
  onAction: (action: DocumentAction) => void;
  onRetry: (documentId: string) => void;
  onNewVersion: (document: Document) => void;
}) {
  return (
    <li className="space-y-3 rounded-xl border border-border p-4" data-testid="knowledge-document">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="break-words font-medium">{document.title || document.name}</h3>
          <p className="mt-1 break-words text-xs text-muted-foreground">
            {document.title && document.title !== document.name ? <>{document.name} · </> : null}
            <Trans>Version {document.version}</Trans>
          </p>
        </div>
        <DocumentStatus document={document} />
      </div>
      {processing(document) && document.currentStage ? (
        <p role="status" className="text-xs text-muted-foreground">
          {document.currentStage}
        </p>
      ) : null}
      {document.error ? (
        <p role="alert" className="break-words text-sm text-destructive">
          {document.error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          nativeButton={false}
          render={<a href={document.sourceUrl} target="_blank" rel="noopener noreferrer" />}
        >
          <Trans>View original</Trans>
        </Button>
        {document.parseStatus === "ready" && document.publicationStatus !== "published" ? (
          <Button size="sm" disabled={busy} onClick={() => onAction({ kind: "publish", document })}>
            <Trans>Publish</Trans>
          </Button>
        ) : null}
        {document.publicationStatus === "published" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onAction({ kind: "withdraw", document })}
          >
            <Trans>Withdraw</Trans>
          </Button>
        ) : null}
        {document.parseStatus === "ready" ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onNewVersion(document)}>
            <Trans>Upload new version</Trans>
          </Button>
        ) : null}
        {document.parseStatus === "failed" || document.parseStatus === "cancelled" ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onRetry(document.id)}>
            <Trans>Retry processing</Trans>
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/** IT manages approved source documents here; mutable agent memory remains separate. */
export function AdminKnowledgePage() {
  const { t } = useLingui();
  const selfId = useQueryUserId();
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const key = ["drok", `user:${selfId ?? ""}`, "admin.knowledge"] as const;
  const [editingConnection, setEditingConnection] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replacing, setReplacing] = useState<Document | null>(null);
  const uploadPanel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (uploading) uploadPanel.current?.scrollIntoView?.({ block: "nearest" });
  }, [uploading, replacing?.id]);
  const [action, setAction] = useState<DocumentAction | null>(null);
  const connection = useQuery({
    queryKey: [...key, "connection"],
    queryFn: () => drokRpc.knowledge.getConnection(),
    enabled: selfId !== null,
  });
  const collections = useQuery({
    queryKey: [...key, "collections"],
    queryFn: () => drokRpc.knowledge.listCollections(),
    enabled: selfId !== null && connection.data !== undefined && connection.data !== null,
  });
  const selected =
    collections.data?.find((item) => item.id === collectionId) ??
    (collectionId ? undefined : collections.data?.[0]);
  const sources = useQuery({
    queryKey: [...key, "source-bots"],
    queryFn: () => drokRpc.templates.sourceBots(),
    enabled: selected !== undefined,
  });
  const documents = useQuery({
    queryKey: [...key, "documents", selected?.id],
    queryFn: async ({ signal }) => {
      const rows = await drokRpc.knowledge.listDocuments(
        { collectionId: selected!.id },
        { signal },
      );
      // Read progress only for in-flight documents, in bounded groups, without reparsing them.
      const active = rows.filter(processing).slice(0, 4);
      const updates = await Promise.all(
        active.map((document) =>
          drokRpc.knowledge.refreshDocument({ documentId: document.id }, { signal }),
        ),
      );
      const byId = new Map(updates.map((document) => [document.id, document]));
      return rows.map((document) => byId.get(document.id) ?? document);
    },
    enabled: selected !== undefined && connection.data?.enabled === true,
    refetchInterval: (query) =>
      !query.state.error && query.state.data?.some(processing) ? 5_000 : false,
  });
  const invalidate = () => client.invalidateQueries({ queryKey: key });
  const saveConnection = useMutation({
    mutationFn: (values: KnowledgeConnectionValues) => drokRpc.knowledge.saveConnection(values),
    onSuccess: async () => {
      setEditingConnection(false);
      await invalidate();
      toast.success(t`Connection saved`);
    },
  });
  const create = useMutation({
    mutationFn: (values: { name: string; description?: string }) =>
      drokRpc.knowledge.createCollection(values),
    onSuccess: async (collection) => {
      setCreating(false);
      setUploading(false);
      await invalidate();
      navigate(`/admin/knowledge/${collection.id}`);
    },
  });
  const upload = useMutation({
    mutationFn: ({
      file,
      metadata,
    }: {
      file: File;
      metadata: { title?: string; version: string; replacesDocumentId?: string };
    }) => uploadDocument(selected!.id, file, metadata),
    onSuccess: async () => {
      setUploading(false);
      await invalidate();
    },
  });
  const bind = useMutation({
    mutationFn: (botIds: string[]) =>
      drokRpc.knowledge.setBindings({ collectionId: selected!.id, botIds }),
    onSuccess: async () => {
      await invalidate();
      toast.success(t`Assistants saved`);
    },
  });
  const changePublication = useMutation({
    mutationFn: (next: DocumentAction) =>
      next.kind === "publish"
        ? drokRpc.knowledge.publishDocument({ documentId: next.document.id })
        : drokRpc.knowledge.withdrawDocument({ documentId: next.document.id }),
    onSuccess: async () => {
      setAction(null);
      await invalidate();
    },
  });
  const retry = useMutation({
    mutationFn: (documentId: string) => drokRpc.knowledge.retryDocument({ documentId }),
    onSuccess: invalidate,
    onError: (error) => toast.error(errorText(error)),
  });
  const showConnection = editingConnection || connection.data === null;
  return (
    <section className="space-y-5" data-testid="admin-knowledge">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold">
          <Trans>Company knowledge</Trans>
        </h2>
        {connection.data && !editingConnection ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              saveConnection.reset();
              setEditingConnection(true);
            }}
          >
            <Trans>Connection</Trans>
          </Button>
        ) : null}
      </div>
      {connection.isPending ? <Spinner aria-label={t`Loading…`} /> : null}
      {connection.isError ? (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>{errorText(connection.error)}</p>
          <Button size="sm" variant="outline" onClick={() => void connection.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      ) : null}
      {showConnection ? (
        <KnowledgeConnectionForm
          connection={connection.data ?? null}
          saving={saveConnection.isPending}
          error={saveConnection.error ? errorText(saveConnection.error) : null}
          onSave={(values) => saveConnection.mutate(values)}
          onCancel={connection.data ? () => setEditingConnection(false) : undefined}
        />
      ) : null}
      {connection.data && !connection.data.enabled ? (
        <p className="text-sm text-muted-foreground">
          <Trans>The knowledge connection is disabled.</Trans>
        </p>
      ) : null}
      {connection.data?.enabled ? (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            {collections.data?.length ? (
              <div className="min-w-0 max-w-full space-y-1">
                <label htmlFor="knowledge-collection" className="text-sm font-medium">
                  <Trans>Collection</Trans>
                </label>
                <NativeSelect
                  id="knowledge-collection"
                  className="w-full sm:w-72"
                  value={selected?.id ?? ""}
                  disabled={upload.isPending || bind.isPending}
                  onChange={(event) => {
                    setUploading(false);
                    upload.reset();
                    bind.reset();
                    navigate(`/admin/knowledge/${event.target.value}`);
                  }}
                >
                  {!selected ? (
                    <NativeSelectOption value="">
                      <Trans>Select a collection</Trans>
                    </NativeSelectOption>
                  ) : null}
                  {collections.data.map((collection) => (
                    <NativeSelectOption key={collection.id} value={collection.id}>
                      {collection.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </div>
            ) : (
              <span />
            )}
            <Button
              variant="outline"
              disabled={creating}
              onClick={() => {
                create.reset();
                setCreating(true);
              }}
            >
              <Trans>New collection</Trans>
            </Button>
          </div>
          {creating ? (
            <KnowledgeCollectionForm
              saving={create.isPending}
              error={create.error ? errorText(create.error) : null}
              onSave={(values) => create.mutate(values)}
              onCancel={() => setCreating(false)}
            />
          ) : null}
          {collections.isPending ? <Spinner aria-label={t`Loading…`} /> : null}
          {collections.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorText(collections.error)}
            </p>
          ) : null}
          {collections.data?.length === 0 && !creating ? (
            <p className="py-6 text-sm text-muted-foreground">
              <Trans>No collections yet</Trans>
            </p>
          ) : null}
          {selected ? (
            <div className="space-y-4" key={selected.id}>
              {selected.description ? (
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {selected.description}
                </p>
              ) : null}
              {sources.isError ? (
                <p role="alert" className="text-sm text-destructive">
                  {errorText(sources.error)}
                </p>
              ) : sources.data ? (
                <KnowledgeBindings
                  key={`${selected.id}:${selected.botIds.join(",")}`}
                  collection={selected}
                  bots={sources.data}
                  saving={bind.isPending}
                  error={bind.error ? errorText(bind.error) : null}
                  onSave={(ids) => bind.mutate(ids)}
                />
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                <h3 className="text-sm font-medium">
                  <Trans>Documents</Trans>
                </h3>
                <Button
                  disabled={uploading}
                  onClick={() => {
                    upload.reset();
                    setReplacing(null);
                    setUploading(true);
                  }}
                >
                  <Trans>Upload document</Trans>
                </Button>
              </div>
              {uploading ? (
                <div ref={uploadPanel}>
                  <KnowledgeUploadForm
                    key={replacing?.id ?? "new"}
                    uploading={upload.isPending}
                    maxUploadSizeBytes={connection.data.maxUploadSizeBytes}
                    replaces={
                      replacing
                        ? { title: replacing.title || replacing.name, version: replacing.version }
                        : null
                    }
                    error={upload.error ? errorText(upload.error) : null}
                    onUpload={(file, metadata) =>
                      upload.mutate({
                        file,
                        metadata: {
                          ...metadata,
                          ...(replacing ? { replacesDocumentId: replacing.id } : {}),
                        },
                      })
                    }
                    onCancel={() => setUploading(false)}
                  />
                </div>
              ) : null}
              {documents.isPending ? <Spinner aria-label={t`Loading…`} /> : null}
              {documents.isError ? (
                <div role="alert" className="space-y-2 text-sm text-destructive">
                  <p>{errorText(documents.error)}</p>
                  <Button size="sm" variant="outline" onClick={() => void documents.refetch()}>
                    <Trans>Try again</Trans>
                  </Button>
                </div>
              ) : null}
              {documents.data?.length === 0 ? (
                <p className="py-6 text-sm text-muted-foreground">
                  <Trans>No documents yet</Trans>
                </p>
              ) : null}
              <ul className="space-y-3">
                {documents.data?.map((document) => (
                  <DocumentCard
                    key={document.id}
                    document={document}
                    busy={changePublication.isPending || retry.isPending}
                    onAction={(next) => {
                      changePublication.reset();
                      setAction(next);
                    }}
                    onRetry={(id) => retry.mutate(id)}
                    onNewVersion={(document) => {
                      upload.reset();
                      setReplacing(document);
                      setUploading(true);
                    }}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
      <AlertDialog
        open={action !== null}
        onOpenChange={(open) => {
          if (!open && !changePublication.isPending) setAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {action?.kind === "publish" ? (
                <Trans>Publish this version?</Trans>
              ) : (
                <Trans>Withdraw this version?</Trans>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {action?.kind === "publish" ? (
                <Trans>
                  Review the original before making this version available to the selected
                  assistants.
                </Trans>
              ) : (
                <Trans>
                  Assistants will stop using this version for new answers. Previous answers remain
                  in their conversations.
                </Trans>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {action ? (
            <div className="space-y-1 text-sm">
              <p className="break-words font-medium">
                {action.document.title || action.document.name}
              </p>
              <p className="text-muted-foreground">
                <Trans>Version {action.document.version}</Trans>
              </p>
              {action.kind === "publish" && action.document.replacesDocumentId ? (
                <p className="text-muted-foreground">
                  <Trans>Publishing will withdraw the version it replaces.</Trans>
                </p>
              ) : null}
              <a
                className="text-foreground underline underline-offset-4"
                href={action.document.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Trans>View original</Trans>
              </a>
            </div>
          ) : null}
          {changePublication.error ? (
            <p role="alert" className="text-sm text-destructive">
              {errorText(changePublication.error)}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={changePublication.isPending}
              onClick={() => setAction(null)}
            >
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <Button
              disabled={changePublication.isPending || !action}
              onClick={() => {
                if (action) changePublication.mutate(action);
              }}
            >
              {changePublication.isPending ? <Spinner aria-hidden="true" /> : null}
              {action?.kind === "publish" ? <Trans>Publish</Trans> : <Trans>Withdraw</Trans>}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
